import styled from 'styled-components';

import { useMutation } from '@tanstack/react-query';

import CancelRecordForm from 'components/patient/CancelRecordForm';
import { ActivityInputValues } from 'components/shared/activityInput/useActivityInput';
import StandardModal, { ColorThemeName, PatientName } from 'components/shared/StandardModal';
import { FormProvider } from 'context/form';
import { FormHelpers, FormMeta, FormValues } from 'context/form/types';
import useInvalidateQueries from 'hooks/useInvalidateQueries';
import { deleteLocationEpisode } from 'services/api/locationEpisodes';
import { useToastActions } from 'stores/toastStore';
import { BodySmall, BodySmallBold } from 'styles/typography';

export type CancelRecordModalProps = {
  patientName: string;
  groupName: string;
  locationEpisodeId: string;
  onCancel: () => void;
  onSuccess: () => void;
};

export type CancelRecordFormValues = {
  note: ActivityInputValues;
};

export type CancelRecordFormMeta = {
  isSubmitting: boolean;
  isUploadingAttachments: boolean;
};

export default function CancelRecordModal({
  patientName,
  groupName,
  locationEpisodeId,
  onCancel,
  onSuccess,
}: CancelRecordModalProps) {
  const invalidateQueries = useInvalidateQueries();
  const { addToast } = useToastActions();

  const { mutate } = useMutation({
    mutationFn: deleteLocationEpisode,
  });

  const handleSubmit = async (
    values: FormValues<CancelRecordFormValues>,
    _meta: FormMeta<CancelRecordFormMeta>,
    helpers: FormHelpers<CancelRecordFormValues, CancelRecordFormMeta>
  ) => {
    helpers.setMeta('isSubmitting', true);

    mutate(
      {
        id: locationEpisodeId,
        note: values.note
          ? {
              ...values.note,
              attachments: values.note.attachments?.map((upload) => upload.serialize()),
            }
          : undefined,
      },
      {
        onSuccess: () => {
          onSuccess();
          addToast({ text: `Record at ${groupName} has been successfully canceled` });
          invalidateQueries({
            episode: true,
            locationEpisode: true,
            activities: true,
          });
        },
        onError: () => {
          addToast({ text: 'Something went wrong. Please try again' });
        },
        onSettled: () => {
          helpers.setMeta('isSubmitting', false);
        },
      }
    );
  };

  return (
    <StandardModal
      closeOnBackdropClick
      colorTheme={ColorThemeName.PrimaryRed}
      onCancel={onCancel}
      title='Confirm Cancelation'>
      <Content>
        <PatientName style={{ marginBottom: 0 }}>Patient: {patientName}</PatientName>
        <BodySmallBold>
          Canceling a record will remove the patient from the Patient Portfolio for {groupName}.
        </BodySmallBold>
        <BodySmall>This will disable the ability to add notes to the patient&apos;s story at {groupName}</BodySmall>
      </Content>
      <FormProvider<CancelRecordFormValues, CancelRecordFormMeta> onSubmit={handleSubmit} resetOnInitialValuesChange>
        <CancelRecordForm onCancel={onCancel} locationEpisodeId={locationEpisodeId} />
      </FormProvider>
    </StandardModal>
  );
}

const Content = styled.div`
  margin: 26px 0 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;
